import axios from "axios"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const buildUrl = (path: string) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL.replace(/\/$/, "")}${path}`
  }
  return path
}

type HttpMethod = "get" | "post" | "patch"

const AREA_ENDPOINTS = ["/api/v1/ptw/org-departments", "/api/v1/org-departments"]
const CODE_DESCR_ENDPOINTS = ["/api/v1/ptw/code-descr", "/api/v1/code-descr"]

async function requestArea<T>({
  method,
  suffix = "",
  data,
  params,
}: {
  method: HttpMethod
  suffix?: string
  data?: any
  params?: Record<string, any>
}): Promise<T> {
  let lastError: any
  for (const base of AREA_ENDPOINTS) {
    try {
      const url = buildUrl(`${base}${suffix}`)
      const response = await axios.request<T>({
        url,
        method,
        data,
        params,
      })
      return response.data
    } catch (error: any) {
      lastError = error
      const status = error?.response?.status
      if (status === 404) {
        continue
      }
      throw error
    }
  }
  throw lastError
}

export interface AreaDto {
  areaId: string
  tenantId: string
  code: string
  areaType: string
  nameTh: string
  nameEn: string
  parentId?: string | null
  parentName?: string | null
  descr?: string | null
  plotPlanFile?: string | null
  latitude?: number | null
  longitude?: number | null
  isActive: boolean
  createdAt?: string | null
  updatedAt?: string | null
}

export interface AreaUpsertRequest {
  code: string
  areaType: string
  nameTh: string
  nameEn: string
  parentId?: string | null
  descr?: string | null
  plotPlanFile?: string | null
  latitude?: number | null
  longitude?: number | null
  isActive: boolean
}

export type AreaStatusFilter = "all" | "active" | "inactive"

export interface PagedResultDto<T> {
  items: T[]
  pageNumber: number
  pageSize: number
  totalItems: number
  totalPages: number
}

interface SearchAreaParams {
  tenantId?: string
  search?: string
  status?: AreaStatusFilter
  page?: number
  pageSize?: number
}

export async function searchAreas({
  search,
  status = "all",
  page = 1,
  pageSize = 20,
  tenantId = DEFAULT_TENANT_ID,
}: SearchAreaParams): Promise<PagedResultDto<AreaDto>> {
  const data = await requestArea<PagedResultDto<AreaDto>>({
    method: "get",
    params: {
      tenantId,
      search,
      status,
      pageNumber: page,
      pageSize,
    },
  })
  if (Array.isArray(data)) {
    return {
      items: data,
      pageNumber: 1,
      pageSize: data.length,
      totalItems: data.length,
      totalPages: 1,
    }
  }
  return data
}

export async function createArea(payload: AreaUpsertRequest, tenantId = DEFAULT_TENANT_ID): Promise<AreaDto> {
  return await requestArea<AreaDto>({
    method: "post",
    data: { ...payload, tenantId },
  })
}

export async function updateArea(areaId: string, payload: AreaUpsertRequest, tenantId = DEFAULT_TENANT_ID): Promise<AreaDto> {
  return await requestArea<AreaDto>({
    method: "post",
    suffix: `/${areaId}`,
    data: { ...payload, tenantId },
  })
}

export async function setAreaActive(areaId: string, isActive: boolean, tenantId = DEFAULT_TENANT_ID): Promise<void> {
  await requestArea<void>({
    method: "patch",
    suffix: `/${areaId}/active`,
    params: { isActive, tenantId },
  })
}

export async function fetchAreaOptions(
  tenantId = DEFAULT_TENANT_ID,
  status: AreaStatusFilter = "active",
): Promise<AreaDto[]> {
  try {
    const pageSize = 500
    let page = 1
    let totalPages = 1
    const collected = new Map<string, AreaDto>()

    do {
      const res = await searchAreas({ tenantId, status, page, pageSize })
      const items = res.items ?? []
      items.forEach((item) => {
        if (item?.areaId) {
          collected.set(item.areaId, item)
        }
      })
      totalPages = Math.max(1, res.totalPages ?? 1)
      page += 1
    } while (page <= totalPages)

    return Array.from(collected.values())
  } catch (error) {
    console.error("fetchAreaOptions failed", error)
    return []
  }
}

export interface CodeDescrOption {
  code: string
  descr?: string | null
}

export async function fetchAreaTypes(tenantId = DEFAULT_TENANT_ID): Promise<CodeDescrOption[]> {
  let lastError: any
  for (const base of CODE_DESCR_ENDPOINTS) {
    try {
      const url = buildUrl(base)
      const response = await axios.get<CodeDescrOption[]>(url, {
        params: { codeG: "areaType", tenantId },
      })
      return response.data ?? []
    } catch (error: any) {
      lastError = error
      const status = error?.response?.status
      if (status === 404) continue
      throw error
    }
  }
  throw lastError
}
